import { Button } from '@/components/common/Button';
import { Checkbox } from '@/components/common/Checkbox';
import Combobox from '@/components/common/Combobox';
import { DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/common/DropdownMenu';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/Select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/common/Tabs';
import Table from '@/components/table/Table';
import { DATE_YMD_HMS_12H_FORMAT, DEFAULT_SELECT_PAGE_SIZES, getShopCategoryName, ROUTES, SHOP_CATEGORY, SHOP_PICKUP_STATUS, SHOP_RATING, SHOP_STATUS } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { LANGUAGES_SUPPORTED, SHOP_CATEGORY_TYPE, SHOP_STATUS_TYPE, ShopStatusType } from '@/types/enums';
import { Shop } from '@/types/shop';
import { formatDateTime } from '@/utils/datetime';
import { toastify } from '@/utils/toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { DropdownMenu } from '@radix-ui/react-dropdown-menu';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import classNames from 'classnames';
import { BanIcon, CheckIcon, CircleCheckIcon, EllipsisIcon, PlusIcon, SearchIcon, SquarePenIcon, StarIcon, Trash2Icon, XIcon } from 'lucide-react';
import { flowResult, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ConfirmModalWithReason from './modals/ConfirmModalWithReason';
import { ActionDropdownItem } from '@/components/general/ActionDropdownItem';
import { Link, useNavigate } from 'react-router-dom';
import AddShopModal from './modals/AddShopModal';
import { scrollToTop } from '@/utils/utils';
import { MultiSelect } from '@/components/common/MultiSelect';

export default observer(function ShopManagementPage() {
    // hooks
    const { t, i18n: { language } } = useTranslation();

    // store
    const {
        shopStore: { totalByStatus, getAllShop, shops, paging, totalRecord, setObservable, keepActivedTab, keepSearchParams, defaultPaging },
        generalStore: { administrativeUnit },
        modalStore: { showModal }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        name: yup
            .string(),
        taxCode: yup
            .string(),
        category: yup.array().of(yup.number()),
        ownerName: yup
            .string(),
        phoneNumber: yup
            .string(),
        provinceName: yup
            .string(),
        provinceCode: yup
            .string(),
        rating: yup.string(),
        pickUpStatus: yup.array().of(yup.boolean())
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const initialValues = {
        name: '',
        taxCode: '',
        category: [],
        ownerName: '',
        phoneNumber: '',
        provinceName: 'all',
        provinceCode: '',
        rating: 'all',
        pickUpStatus: []
    };

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: initialValues
    });

    // State
    const [activeTab, setActiveTab] = useState(keepActivedTab ? keepActivedTab : '');
    const [searchState, setSearchState] = useState<Object>(keepSearchParams ?? '');

    // lifecycle
    useEffect(() => {
        if (keepSearchParams) {
            form.reset(keepSearchParams);
            onFetch(keepSearchParams, paging);
        }
    }, []);

    useEffect(() => {
        onFetch(searchState);
    }, [activeTab]);

    // function
    const onSearch = (searchParams: FormData) => {
        setSearchState(searchParams);
        onFetch(searchParams, defaultPaging);
    };

    const onFetch = (searchParams?: any, paging?: TableState) => {
        if (paging) {
            setObservable('paging', paging ?? null);
        }

        setObservable('keepActivedTab', activeTab);
        setObservable('keepSearchParams', toJS(searchParams));
        getAllShop(convertFormdataToPayload(searchParams), paging);
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging = stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        onFetch(searchState, newPaging);
        scrollToTop();
    };

    const handleTabChange = (tabValue: string) => {
        setActiveTab(tabValue);
    };

    const handleClickAddShop = () => {
        showModal({
            size: '4xl',
            title: t('words_title.add_shop'),
            content: <AddShopModal onFetch={onFetch} />,
            footerClassName: 'hidden'
        });
    };

    const getIconByCategoryIds = (cateIds) => {
        let text = '';

        if (cateIds && cateIds.length > 0) {
            text = cateIds.reduce((acc: string[], cur) => {
                acc.push(t(`select_options.${getShopCategoryName(cur)}`));
                return acc;
            }, []).join(' + ') ?? '';
        }

        return text.replace(/\+ Pet/g, '+');
    };

    const convertFormdataToPayload = (data: FormData) => {
        const { name, taxCode, ownerName, category, phoneNumber, provinceCode, rating, pickUpStatus } = data ?? {};
        const [alreadyOffer, notOffer] = pickUpStatus ?? [];
        const pickUpStatusConvert = alreadyOffer && notOffer ?
            [true, false] :
            alreadyOffer ?
                [true] :
                notOffer ?
                    [false] :
                    [];
        return {
            name,
            taxCode,
            categories: (category && category.length > 0) ? category : [],
            ownerName,
            phoneNumber,
            provinceCode,
            rating: (rating && Object.keys(SHOP_RATING).find(item => item === rating)),
            pickupStatus: pickUpStatusConvert,
            status: activeTab
        };
    };

    // columns
    const columns: ColumnDef<Shop>[] = [
        {
            header: t('words_title.index'),
            size: 10,
            enableSorting: false,
            cell: ({ row: { index } }) => (
                <div>{paging.pagination.pageIndex * paging.pagination.pageSize + index + 1}</div>
            )
        },
        {
            accessorKey: 'name',
            header: t('words_title.shop_name'),
            enableSorting: false,
            size: 200,
            cell: ({ row: { original } }) => (
                <div>{original?.name ?? ''}</div>
            )
        },
        {
            accessorKey: 'taxCode',
            header: t('words_title.tax_code'),
            enableSorting: false,
            size: 90,
            cell: ({ row: { original } }) => (
                <div>{original?.taxCode ?? ''}</div>
            )
        },
        {
            accessorKey: 'ownerName',
            header: t('words_title.owner_name'),
            enableSorting: false,
            size: 100,
            cell: ({ row: { original } }) => (
                <>
                    {
                        original?.user?.isDeleted ?
                            (
                                <div>{original?.user?.fullName ?? ''}</div>
                            ) :
                            (
                                <Link to={`/users/${original.user.code}`} target='blank' rel='noopener noreferrer' className='underline text-link-1 cursor-pointer'>
                                    {original?.user?.fullName ?? ''}
                                </Link>
                            )
                    }
                </>
            )
        },
        {
            accessorKey: 'phoneNumber',
            header: t('words_title.phone_number'),
            enableSorting: false,
            size: 80,
            cell: ({ row: { original } }) => (
                <div>{original?.phoneNumber ?? ''}</div>
            )
        },
        {
            accessorKey: 'city',
            header: t('words_title.city'),
            enableSorting: false,
            size: 100,
            cell: ({ row: { original } }) => {
                const province = toJS(administrativeUnit).provinces.find(item => (
                    item.code.toString() === original.province.code
                ));

                return (
                    <div>
                        {province ?
                            (
                                language === LANGUAGES_SUPPORTED.English ? province.nameEn : province.name
                            ) :
                            ''}
                    </div>
                );
            }
        },
        {
            accessorKey: 'category',
            header: t('words_title.category'),
            enableSorting: false,
            size: 80,
            cell: ({ row: { original } }) => (
                <div>{original?.categoryIds ? getIconByCategoryIds(original.categoryIds) : ''}</div>
            )
        },
        {
            accessorKey: 'pickUpStatus',
            header: t('words_title.pickup_service'),
            enableSorting: false,
            size: 80,
            cell: ({ row: { original } }) => (
                <div className='flex items-center'>
                    <div>{original.pickUpStatus ? <CheckIcon className='h-4.5 w-4.5 text-accept-action' /> : ''}</div>
                </div>
            )
        },
        {
            accessorKey: 'rating',
            header: t('words_title.shop_rating'),
            enableSorting: false,
            size: 80,
            cell: ({ row: { original } }) => (
                <div>
                    {original.rating !== 0 ?
                        (
                            <div className='flex items-center gap-1'>
                                <span>{original?.rating?.toFixed(1)}</span>
                                <StarIcon className='fill-star-1 text-star-1 flex-none' />
                            </div>
                        ) :
                        ''}
                </div>
            )
        },
        {
            accessorKey: 'createdAt',
            header: t('words_title.created_date'),
            enableSorting: false,
            size: 100,
            cell: ({ row: { original } }) => (
                <div>{original.createdAt ? formatDateTime(original.createdAt, DATE_YMD_HMS_12H_FORMAT) : ''}</div>
            )
        },
        {
            accessorKey: 'status',
            header: t('words_title.status'),
            enableSorting: false,
            size: 100,
            cell: ({ row: { original } }) => (
                <ShopStatusColumn type={original.status} />
            )
        },
        {
            id: 'actions',
            size: 10,
            cell: ({ row: { original } }) => (
                <div className='flex justify-center items-center'>
                    <ActionMenuDropdown status={original.status} code={original.code} onFetch={onFetch} />
                </div>
            )
        }
    ];

    const CATEGORY_COMBOBOX: { value: number[], key: number, label: string }[] = [
        {
            value: [SHOP_CATEGORY_TYPE.PetShop],
            key: 1,
            label: t('select_options.category_pet_shop')
        },
        {
            value: [SHOP_CATEGORY_TYPE.PetHospital],
            key: 2,
            label: t('select_options.category_pet_hospital')
        },
        {
            value: [SHOP_CATEGORY_TYPE.Retail],
            key: 3,
            label: t('select_options.category_retail')
        },
        {
            value: [SHOP_CATEGORY_TYPE.PetShop, SHOP_CATEGORY_TYPE.PetHospital],
            key: 4,
            label: t('select_options.category_pet_shop__pet_hospital')
        },
        {
            value: [SHOP_CATEGORY_TYPE.PetHospital, SHOP_CATEGORY_TYPE.Retail],
            key: 5,
            label: t('select_options.category_pet_hospital__pet_retail')
        },
        {
            value: [SHOP_CATEGORY_TYPE.PetShop, SHOP_CATEGORY_TYPE.Retail],
            key: 6,
            label: t('select_options.category_pet_shop__pet_retail')
        },
        {
            value: [SHOP_CATEGORY_TYPE.PetShop, SHOP_CATEGORY_TYPE.PetHospital, SHOP_CATEGORY_TYPE.Retail],
            key: 7,
            label: t('select_options.category_pet_shop__pet_hospital__pet_retail')
        }
    ];

    return (
        <div>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSearch)}>
                    <div className='bg-white rounded p-4'>
                        <div className='grid grid-cols-2 gap-x-20 gap-y-4'>

                            <FormField
                                control={form.control}
                                name='name'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.shop_name')}</Label>
                                            <FormControl className='w-4/5'>
                                                <Input type='text' {...field} />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='taxCode'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.tax_code')}</Label>
                                            <FormControl className='w-4/5'>
                                                <Input
                                                    type='number'
                                                    isTypingOnlyNumber={true}
                                                    {...field}
                                                />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='category'
                                render={({ field, fieldState }) => (
                                    <FormItem className='flex items-center'>
                                        <Label className='w-1/5 label-1'>
                                            {t('words_title.category')}
                                        </Label>
                                        <FormControl>
                                            <MultiSelect
                                                options={CATEGORY_COMBOBOX.map(item => ({
                                                    label: item.label,
                                                    value: item.key
                                                }))}
                                                fieldState={fieldState}
                                                value={(field.value || []).filter((v): v is number => v !== undefined)}
                                                onChange={selectedValues => field.onChange(selectedValues)}
                                                placeholder=''
                                                className='w-4/5'
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='ownerName'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.owner_name')}</Label>
                                            <FormControl className='w-4/5'>
                                                <Input type='text' {...field} />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='phoneNumber'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.phone_number')}</Label>
                                            <FormControl className='w-4/5'>
                                                <Input
                                                    type='number'
                                                    isTypingOnlyNumber={true}
                                                    {...field}
                                                />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='provinceName'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.city')}</Label>
                                            <FormControl className='w-4/5'>
                                                <Combobox
                                                    placeholder={t('placeholder.choose')}
                                                    value={field.value ?? ''}
                                                    onChange={(item) => {
                                                        field.onChange(item?.value);
                                                        form.setValue('provinceCode', item?.code);
                                                    }}
                                                    items={[
                                                        {
                                                            label: t('words_title.all'),
                                                            value: 'all'
                                                        },
                                                        ...(Array.isArray(administrativeUnit?.provinces) ?
                                                            administrativeUnit.provinces.map(item => ({
                                                                label: language === LANGUAGES_SUPPORTED.English ? item.fullNameEn : item.fullName,
                                                                value: item.name,
                                                                code: item.code
                                                            })) :
                                                            []
                                                        )
                                                    ]}
                                                    required
                                                    side='top'
                                                />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='rating'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.shop_rating')}</Label>
                                            <Select onValueChange={field.onChange} value={field.value} className='w-4/5'>
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder={t('placeholder.choose')} />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem
                                                        className='hover:cursor-pointer'
                                                        value='all'
                                                    >
                                                        <span>{t('words_title.all')}</span>
                                                    </SelectItem>
                                                    {Object.keys(SHOP_RATING).map(item => (
                                                        <SelectItem
                                                            className='hover:cursor-pointer'
                                                            key={SHOP_RATING[item].key}
                                                            value={item}
                                                        >
                                                            <span>{t(`words_title.${SHOP_RATING[item].key}`)}</span>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className='flex items-center'>
                                <Label className='w-1/5 label-1'>{t('words_title.pickup_status')}</Label>
                                <div className='w-4/5 flex items-center gap-8'>
                                    {
                                        Object.keys(SHOP_PICKUP_STATUS).map((item, index) => (
                                            <FormField
                                                key={index}
                                                control={form.control}
                                                name={`pickUpStatus.${index}`}
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <div className='flex gap-2 items-center'>
                                                            <FormControl>
                                                                <Checkbox
                                                                    checked={field.value}
                                                                    onCheckedChange={field.onChange}
                                                                />
                                                            </FormControl>
                                                            <Label>{t(`words_title.${SHOP_PICKUP_STATUS[item].key}`)}</Label>
                                                        </div>
                                                    </FormItem>
                                                )}
                                            />
                                        ))
                                    }
                                </div>
                            </div>

                        </div>
                        <div className='flex justify-center mt-8'>
                            <Button variant='outline' className='min-w-48 flex items-center justify-center gap-2 group'>
                                <SearchIcon className='w-5 h-5 text-active group-hover:text-white' />
                                <span>{t('button.search')}</span>
                            </Button>
                        </div>
                    </div>
                </form>
            </Form>

            <Tabs value={activeTab} onValueChange={handleTabChange} className='w-full h-full flex flex-col'>
                <div className='bg-white mt-4 rounded p-4'>
                    <Button variant='default' className='text-white flex items-center justify-center gap-2 min-w-28 ml-auto' onClick={handleClickAddShop}>
                        <PlusIcon className='w-7 h-7 text-white' />
                        <span>{t('button.add_shop')}</span>
                    </Button>
                    <TabsList variant='shop' data-orientation='horizontal' className='flex w-full mt-6'>
                        <TabsTrigger
                            variant='shop'
                            className='md:text-[0.875rem] text-3 w-full'
                            value=''
                        >
                            {`${t('words_title.all_shop')} (${Object.values(totalByStatus).reduce((accumulator, value) => accumulator += value, 0)})`}
                        </TabsTrigger>
                        {
                            Object.entries([
                                SHOP_STATUS[SHOP_STATUS_TYPE.WaitApprove],
                                SHOP_STATUS[SHOP_STATUS_TYPE.Active],
                                SHOP_STATUS[SHOP_STATUS_TYPE.Inactive],
                                SHOP_STATUS[SHOP_STATUS_TYPE.DeniedApprove],
                                SHOP_STATUS[SHOP_STATUS_TYPE.NotCompletedInfo]
                            ]).map(([key, value], index) => (
                                <TabsTrigger
                                    key={index}
                                    variant='shop'
                                    className='md:text-[0.875rem] text-3 w-full'
                                    value={value.value.toString()}
                                >
                                    {`${t(`words_title.${value.key}`)} (${totalByStatus?.[value.value] ?? '0'})`}
                                </TabsTrigger>
                            ))
                        }
                    </TabsList>
                </div>

                <div className='bg-white rounded p-4'>
                    <TabsContent value=''>
                        <Table
                            columns={columns}
                            data={shops}
                            rowCount={totalRecord}
                            onStateChange={onStateChange}
                            state={paging}
                            selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                            getRowId={row => row.id?.toString()}
                        />
                    </TabsContent>
                    {
                        Object.entries([
                            SHOP_STATUS[SHOP_STATUS_TYPE.WaitApprove],
                            SHOP_STATUS[SHOP_STATUS_TYPE.Active],
                            SHOP_STATUS[SHOP_STATUS_TYPE.Inactive],
                            SHOP_STATUS[SHOP_STATUS_TYPE.DeniedApprove],
                            SHOP_STATUS[SHOP_STATUS_TYPE.NotCompletedInfo]
                        ]).map(([key, value], index) => (
                            <TabsContent value={value.value.toString()} key={index}>
                                <Table
                                    columns={columns}
                                    data={shops}
                                    rowCount={totalRecord}
                                    onStateChange={onStateChange}
                                    state={paging}
                                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                                    getRowId={row => row.id?.toString()}
                                />
                            </TabsContent>
                        ))
                    }
                </div>
            </Tabs>
        </div>
    );
});

interface ShopStatusColumnProps {
    type: ShopStatusType
}

const ShopStatusColumn = ({ type }: ShopStatusColumnProps) => {
    // hooks
    const { t } = useTranslation();

    return (
        <div className='flex items-center justify-start gap-2'>
            <div className={classNames('w-2 h-2 rounded-full flex-none', SHOP_STATUS[type].color)}></div>
            <div>{t(`words_title.${SHOP_STATUS[type].key}`)}</div>
        </div>
    );
};

interface ActionMenuDropdownProps {
    status: number,
    code: string,
    onFetch: Function
}

const ActionMenuDropdown = ({ status, code, onFetch }: ActionMenuDropdownProps) => {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();

    // store
    const {
        shopStore: { updateStatus, deleteShop, paging, keepSearchParams },
        modalStore: { showAlertModal, modalWaringAlertOptions }
    } = useStore();

    // function
    const handleClickAccept = async () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            content: t('messages.confirm_accept_shop'),
            onSave: async () => {
                const res = await flowResult(updateStatus(code, { status: SHOP_STATUS_TYPE.Active }));
                if (res && res.ok) {
                    onFetch(keepSearchParams, paging);
                    toastify('success', t('messages.update_success'));
                }
            }
        }));
    };

    const handleClickActive = async () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            content: t('messages.confirm_active_shop'),
            onSave: async () => {
                const res = await flowResult(updateStatus(code, { status: SHOP_STATUS_TYPE.WaitApprove }));
                if (res && res.ok) {
                    onFetch(keepSearchParams, paging);
                    toastify('success', t('messages.update_success'));
                }
            }
        }));
    };

    const handleClickDenied = () => {
        showAlertModal(modalWaringAlertOptions({
            size: 'xl',
            hideIconCenter: true,
            contentClassName: 'text-start',
            content: <ConfirmModalWithReason text={t('messages.confirm_denied_shop')} submitFunction={handleSubmitDeniedModal} />,
            footerClassName: 'p-0',
            footer: <></>
        }));
    };

    const handleSubmitDeniedModal = async ({ reason }, cb) => {
        const res = await flowResult(updateStatus(code, {
            status: SHOP_STATUS_TYPE.DeniedApprove,
            reason
        }));
        if (res?.ok) {
            onFetch(keepSearchParams, paging);
            cb && cb();
            toastify('success', t('messages.update_success'));
        }
    };

    const handleClickInactive = () => {
        showAlertModal(modalWaringAlertOptions({
            size: 'xl',
            hideIconCenter: true,
            contentClassName: 'text-start',
            content: <ConfirmModalWithReason text={t('messages.confirm_inactive_shop')} submitFunction={handleSubmitInactiveModal} />,
            footerClassName: 'p-0',
            footer: <></>
        }));
    };

    const handleSubmitInactiveModal = async ({ reason }, cb) => {
        const res = await flowResult(updateStatus(code, {
            status: SHOP_STATUS_TYPE.Inactive,
            reason
        }));
        if (res?.ok) {
            onFetch(keepSearchParams, paging);
            cb && cb();
            toastify('success', t('messages.update_success'));
        }
    };

    const handleClickDelete = async () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            content: t('messages.confirm_delete_shop'),
            onSave: async () => {
                const res = await flowResult(deleteShop(code));
                if (res && res.ok) {
                    onFetch(keepSearchParams, paging);
                    toastify('success', t('messages.delete_success'));
                }
            }
        }));
    };

    const handleClickEdit = () => {
        navigate(`${ROUTES.shopManagement.href}/${code}`);
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <EllipsisIcon className='h-6 w-6 text-active hover:cursor-pointer' />
            </DropdownMenuTrigger>

            <DropdownMenuContent className='w-48' align='end' forceMount>

                {
                    status === SHOP_STATUS_TYPE.WaitApprove && (
                        <>
                            <DropdownMenuItem className='hover:cursor-pointer' asChild>
                                <ActionDropdownItem icon={CheckIcon} text={t('words_title.accept_action')} iconColor='text-accept-action' onClick={handleClickAccept} />
                            </DropdownMenuItem>

                            <DropdownMenuSeparator />
                            <DropdownMenuItem className='hover:cursor-pointer' asChild>
                                <ActionDropdownItem icon={XIcon} text={t('words_title.denied_action')} iconColor='text-denied-action' onClick={handleClickDenied} />
                            </DropdownMenuItem>
                        </>
                    )
                }

                {
                    status === SHOP_STATUS_TYPE.Inactive && (
                        <>
                            <DropdownMenuItem className='hover:cursor-pointer' asChild>
                                <ActionDropdownItem icon={CircleCheckIcon} text={t('words_title.active_action')} onClick={handleClickActive} />
                            </DropdownMenuItem>
                        </>
                    )
                }

                {
                    status === SHOP_STATUS_TYPE.Active && (
                        <>
                            <DropdownMenuItem className='hover:cursor-pointer' asChild>
                                <ActionDropdownItem icon={BanIcon} text={t('words_title.inactive_action')} onClick={handleClickInactive} />
                            </DropdownMenuItem>
                        </>
                    )
                }

                {
                    status !== SHOP_STATUS_TYPE.DeniedApprove && status !== SHOP_STATUS_TYPE.NotCompletedInfo && (
                        <DropdownMenuSeparator />
                    )
                }
                <DropdownMenuItem className='hover:cursor-pointer' asChild>
                    <ActionDropdownItem icon={SquarePenIcon} text={t('words_title.edit_action')} onClick={handleClickEdit} />
                </DropdownMenuItem>

                <DropdownMenuSeparator />
                <DropdownMenuItem className='hover:cursor-pointer' asChild>
                    <ActionDropdownItem icon={Trash2Icon} text={t('words_title.delete_action')} onClick={handleClickDelete} />
                </DropdownMenuItem>

            </DropdownMenuContent>
        </DropdownMenu>
    );
};
